import type { Chord } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';
import { rootPitchClass, nearestScaleDegreeForPitchClass } from '../pitch-utils';

/**
 * sidestep — chromatic mediant. Replaces the selected chord with a major
 * triad a major third below the previous chord's root.
 *
 * Root fidelity: unlike take-the-long-way-home, this move's whole premise
 * is a non-diatonic target — a major third below an arbitrary root only
 * rarely lands on a diatonic scale degree. Restricting applicability to
 * exact matches (as move 6 does) would make this move almost never fire, so
 * it always applies when there's a previous chord and accepts
 * nearestScaleDegreeForPitchClass's nearest-diatonic-degree approximation.
 * The resulting root can be off from the "true" chromatic-mediant pitch by
 * up to a whole step. This is the most significant fidelity gap surfaced by
 * this task; see pitch-utils.ts for the underlying modeling constraint.
 */
export const sidestep: Move = {
  id: 'sidestep',
  name: 'sidestep',
  theory: 'chromatic mediant',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    const prev = ctx.chords[ctx.index - 1];
    return !!chord && !!prev;
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const prev = ctx.chords[ctx.index - 1];

    const prevRootPc = rootPitchClass(ctx.key, prev.scaleDegree, ctx.mode);
    const targetPc = (prevRootPc - 4 + 12) % 12; // major third below
    const { degree } = nearestScaleDegreeForPitchClass(targetPc, ctx.key, ctx.mode);

    const changed: Chord = {
      ...chord,
      scaleDegree: degree,
      quality: 'major',
      extensions: {},
      isChromatic: true,
      chromaticType: 'borrowed',
      key: ctx.key,
      mode: ctx.mode,
    };
    const chords: Chord[] = ctx.chords.map((c, i) => (i === ctx.index ? changed : c));

    return {
      chords,
      changedIds: [changed.id],
      explanation: "stepped sideways into a color the key doesn't own.",
      theoryLabel: 'chromatic mediant',
    };
  },
};
